export function mergeOverlappingRanges(ranges) {
    if (!ranges.length) {
        return []
    }

    const sorted = [...ranges].sort((a, b) => a[0] - b[0])
    const merged = [[...sorted[0]]]

    for (let i = 1; i < sorted.length; i++) {
        const current = sorted[i]
        const last = merged[merged.length - 1]

        if (current[0] <= last[1]) {
            last[1] = Math.max(last[1], current[1])
        } else {
            merged.push([...current])
        }
    }

    return merged
}

export function subtractRangeFromRanges(ranges, rangeToSubtract) {
    const result = []

    for (const range of ranges) {
        const [rangeStart, rangeEnd] = range
        const [cutStart, cutEnd] = rangeToSubtract

        // Range is completely cut
        if (cutStart <= rangeStart && cutEnd >= rangeEnd) {
            continue
        }
        // Cut overlaps start of range
        if (cutStart <= rangeStart && cutEnd >= rangeStart && cutEnd < rangeEnd) {
            result.push([cutEnd, rangeEnd])
        } else if (cutStart > rangeStart && cutStart <= rangeEnd && cutEnd >= rangeEnd) {
            // Cut overlaps end of range
            result.push([rangeStart, cutStart])
        } else if (cutStart > rangeStart && cutEnd < rangeEnd) {
            // Cut is inside range - split into two
            result.push([rangeStart, cutStart])
            result.push([cutEnd, rangeEnd])
        } else {
            // No overlap
            result.push(range)
        }
    }

    return result.filter(r => r.length === 2 && r[0] !== r[1])
}

export function splitLinearLocationByOverlaps(featureLinearLocation, existingFeatures) {
    if (!featureLinearLocation || !existingFeatures || existingFeatures.length === 0) {
        return [{ linearLocation: featureLinearLocation, existingFeatureProperties: null }]
    }

    const featureMin = Math.min(featureLinearLocation.start, featureLinearLocation.end)
    const featureMax = Math.max(featureLinearLocation.start, featureLinearLocation.end)
    const inverted = featureLinearLocation.start > featureLinearLocation.end
    const road = featureLinearLocation.road

    // Find all overlapping ranges from existing features
    const overlaps = []
    for (const existingFeature of existingFeatures) {
        const existingLoc = existingFeature.properties?.linearLocation?.[0] || existingFeature.linear_location?.[0]
        if (!existingLoc || existingLoc.road !== road) {
            continue
        }
        const existingFeatureInverted = existingLoc.start > existingLoc.end
        if (existingFeatureInverted !== inverted) {
            continue
        }

        const existingMin = Math.min(existingLoc.start, existingLoc.end)
        const existingMax = Math.max(existingLoc.start, existingLoc.end)

        if (existingMin < featureMax && featureMin < existingMax) {
            overlaps.push({
                start: Math.max(featureMin, existingMin),
                end: Math.min(featureMax, existingMax),
                properties: existingFeature.properties,
            })
        }
    }

    if (overlaps.length === 0) {
        return [{ linearLocation: featureLinearLocation, existingFeatureProperties: null }]
    }

    overlaps.sort((a, b) => a.start - b.start)

    // Build segments with gaps (non-overlapping) and overlaps
    const segments = []
    let currentPos = featureMin

    for (const overlap of overlaps) {
        // Non-overlapping segment before this overlap
        if (currentPos < overlap.start) {
            segments.push({
                linearLocation: {
                    road,
                    start: inverted ? overlap.start : currentPos,
                    end: inverted ? currentPos : overlap.start,
                },
                existingFeatureProperties: null,
            })
        }

        // Overlapping segment
        const segmentStart = Math.max(currentPos, overlap.start)
        const segmentEnd = overlap.end
        if (segmentStart < segmentEnd) {
            segments.push({
                linearLocation: {
                    road,
                    start: inverted ? segmentEnd : segmentStart,
                    end: inverted ? segmentStart : segmentEnd,
                },
                existingFeatureProperties: overlap.properties,
            })
        }

        currentPos = Math.max(currentPos, overlap.end)
    }

    // Remaining non-overlapping segment at the end
    if (currentPos < featureMax) {
        segments.push({
            linearLocation: {
                road,
                start: inverted ? featureMax : currentPos,
                end: inverted ? currentPos : featureMax,
            },
            existingFeatureProperties: null,
        })
    }

    return segments
}
